import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

/**
 * One parent testimonial.
 *
 * Props
 *   quote:     the full quote (string)
 *   excerpt:   optional shorter pull-quote; defaults to the first ~220 chars of `quote`
 *   name:      parent name (string)
 *   grade:     e.g. "Mother of Grade 9"
 *   school:    optional school name
 *   city:      optional city
 *   testId:    data-testid for QA
 */
export default function Testimonial({ quote, excerpt, name, grade, school, city, testId }) {
    const [expanded, setExpanded] = useState(false);

    const TRIM_LIMIT = 320;
    const trimmed =
        excerpt ||
        (quote.length > TRIM_LIMIT ? quote.slice(0, TRIM_LIMIT - 10).trimEnd() + "…" : quote);
    const isLong = quote.length > trimmed.length;
    const shown = expanded ? quote : trimmed;
    const initial = (name || "P").trim().charAt(0).toUpperCase();

    return (
        <figure
            className="bg-white rounded-3xl border border-aq-border p-6 md:p-8 shadow-sm flex flex-col"
            data-testid={testId}
        >
            <div className="font-serif text-aq-gold text-4xl leading-none mb-2">“</div>
            <blockquote className="font-serif text-aq-navy text-lg md:text-xl leading-snug mb-2 whitespace-pre-line">
                {shown}
            </blockquote>

            {isLong && (
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    data-testid={`${testId}-toggle`}
                    className="self-start inline-flex items-center gap-1 text-aq-gold font-semibold text-sm hover:text-aq-navy transition-colors mt-1 mb-4"
                >
                    {expanded ? (
                        <>
                            Show less <ChevronUp className="w-4 h-4" />
                        </>
                    ) : (
                        <>
                            Read full review <ChevronDown className="w-4 h-4" />
                        </>
                    )}
                </button>
            )}

            <figcaption className="flex items-center gap-3 mt-auto pt-4 border-t border-aq-border">
                <div className="w-10 h-10 rounded-full bg-aq-navy text-aq-gold font-bold flex items-center justify-center text-sm shrink-0">
                    {initial}
                </div>
                <div className="leading-tight">
                    <div className="font-semibold text-aq-navy text-sm">{name}</div>
                    <div className="text-xs text-aq-muted">
                        {grade}
                        {school ? ` · ${school}` : ""}
                        {city ? ` · ${city}` : ""}
                    </div>
                </div>
            </figcaption>
        </figure>
    );
}
